import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SelectLocationFull, SelectNode } from '@workspace/db/extra'
import { useRef } from 'react'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'
import { locationQueryKey, useLocationSelect } from './use-location'

// ---- Reads -----------------------------------------------------------------

const phaseStatusSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('complete'), completedAt: z.string() }),
  z.object({
    status: z.literal('running'),
    workflowId: z.string(),
    progress: z.object({ expected: z.number(), completed: z.number(), failed: z.number() }).optional(),
  }),
  z.object({ status: z.literal('blocked'), blockedBy: z.array(z.string()) }),
  z.object({ status: z.literal('ready') }),
  z.object({ status: z.literal('not_applicable') }),
])

const jobRecordSchema = z.object({
  jobId: z.string(),
  action: z.string(),
  label: z.string(),
  locationId: z.string(),
  status: z.enum(['running', 'completed', 'failed']),
  startedAt: z.string(),
  completedAt: z.string().optional(),
  error: z.string().optional(),
})

const lifecycleSchema = z.enum(['CONFIGURING', 'DISPATCHING', 'PROVISIONED'])

// GET /v1/provision/:locationId/state. Mirrors the v1 UI's `ProvisioningState` (lifecycle/jobs/replacingSerial optional).
const provisioningStateEnvelopeSchema = z.object({
  success: z.literal(true),
  data: z.object({
    locationId: z.string(),
    lifecycle: lifecycleSchema.optional(),
    phases: z.record(z.string(), phaseStatusSchema),
    jobs: z.array(jobRecordSchema).optional(),
    replacingSerial: z.string().nullable().optional(),
  }),
})

export type PhaseStatus = z.infer<typeof phaseStatusSchema>
export type JobRecord = z.infer<typeof jobRecordSchema>
export interface ProvisioningState {
  locationId: string
  lifecycle: z.infer<typeof lifecycleSchema>
  phases: Record<string, PhaseStatus>
  jobs: JobRecord[]
  replacingSerial: string | null
}

/** Pure: apply the v1 defaults (lifecycle CONFIGURING, jobs [], replacingSerial null). */
export function normalizeProvisioningState(
  raw: z.infer<typeof provisioningStateEnvelopeSchema>['data'],
): ProvisioningState {
  return {
    locationId: raw.locationId,
    lifecycle: raw.lifecycle ?? 'CONFIGURING',
    phases: raw.phases,
    jobs: raw.jobs ?? [],
    replacingSerial: raw.replacingSerial ?? null,
  }
}

export const provisioningStateQueryKey = (locationId: string) => ['provisioning-state', locationId] as const

/** Roles allowed to mutate lifecycle (manifest `MANAGE_ROLES`). Gate controls with this. */
export const MANAGE_ROLES = ['admin', 'manager', 'netops'] as const

/** Pure: all nodes (managed devices) across a location's network profiles. */
export function locationNodes(loc: SelectLocationFull | null): SelectNode[] {
  return (loc?.networkProfiles ?? []).flatMap((np) => np.nodes ?? [])
}

/** Nodes (managed devices) of a location, from the shared location read (`networkProfiles[].nodes`). */
export function useLocationNodes(idOrCode: string) {
  return useLocationSelect(idOrCode, locationNodes)
}

/**
 * Provisioning lifecycle state (phase rail, jobs). REST only: v1 also opens a WebSocket (`/provision/:id/ws`);
 * that live push is DEFERRED to the BFF-parity wave, so this polls every 5s while any job/phase is running.
 */
export function useProvisioningState(idOrCode: string) {
  const api = useApi()
  const preview = usePreviewData()
  const locationId = useLocationSelect(idOrCode, (loc) => loc?.id ?? null).data ?? null
  return useQuery({
    queryKey: preview ? ['preview', 'provisioning-state'] : provisioningStateQueryKey(locationId ?? idOrCode),
    queryFn: async (): Promise<ProvisioningState> => {
      if (!locationId) throw new Error('Location not found')
      const res = await api.get(
        `/v1/provision/${encodeURIComponent(locationId)}/state`,
        provisioningStateEnvelopeSchema,
      )
      return normalizeProvisioningState(res.data)
    },
    enabled: !preview && locationId !== null,
    refetchInterval: (q) =>
      q.state.data?.jobs.some((j) => j.status === 'running') ||
      Object.values(q.state.data?.phases ?? {}).some((p) => p.status === 'running')
        ? 5000
        : false,
    ...(preview ? { initialData: preview.provisioningState, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

// ---- Onboarding body (ported from v1 `buildOnboardingBody`) -------------------

export type OnboardVendor = 'meraki' | 'cloudflare' | 'extreme' | 'cradlepoint' | 'supermicro'

export interface OnboardInput {
  vendor: OnboardVendor
  /** Meraki/CF/Extreme: serial. Cradlepoint: cpId. Supermicro: MAC (any separator style). */
  serialNumber: string
  /** Meraki: 'edge' | 'switch' | 'ap' style key resolved by caller into `networkType`; Extreme: 'switch' | 'ap'. */
  deviceType?: string
  /** Meraki only: network the design resolved from deviceType (default 'EDGE'). API enum is EDGE | SWITCH; 'AP' maps to SWITCH. */
  networkType?: string
}

/** Pure: the API's `networkType` enum is EDGE | SWITCH only (an AP lives on the SWITCH network, as in v1). */
export const toApiNetworkType = (networkType?: string): 'EDGE' | 'SWITCH' => {
  const t = networkType?.toUpperCase()
  return t === 'SWITCH' || t === 'AP' ? 'SWITCH' : 'EDGE'
}

/** Pure: build the POST /v1/device-onboarding body for a vendor. */
export function buildOnboardingBody(input: OnboardInput, storeCode: string, orgSlug: string): Record<string, unknown> {
  const body: Record<string, unknown> = { vendor: input.vendor, storeCode, orgSlug }
  const id = input.serialNumber.trim()
  switch (input.vendor) {
    case 'meraki':
      body.device = { serial: id }
      body.networkType = toApiNetworkType(input.networkType)
      body.applyVlanConfigs = true
      break
    case 'cloudflare':
      body.device = { serial: id }
      body.applyLanConfigs = true
      break
    case 'extreme':
      // No apIndex: the server numbers APs itself.
      body.device = { serial: id, deviceType: input.deviceType === 'switch' ? 'SWITCH' : 'AP' }
      break
    case 'cradlepoint':
      body.device = { cpId: id }
      break
    case 'supermicro': {
      const raw = id.toLowerCase().replace(/[:\-.]/g, '')
      body.device = { mac: raw.match(/.{1,2}/g)?.join(':') ?? raw }
      break
    }
  }
  return body
}

// ---- Mutations -----------------------------------------------------------------

const okEnvelopeSchema = z.object({ success: z.boolean() }).passthrough()

/** Invalidates everything a lifecycle write can change. */
function useInvalidateLocation() {
  const qc = useQueryClient()
  return async (idOrCode: string, locationId?: string) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: locationQueryKey(idOrCode) }),
      qc.invalidateQueries({ queryKey: locationId ? provisioningStateQueryKey(locationId) : ['provisioning-state'] }),
      qc.invalidateQueries({ queryKey: ['network-overview'] }),
    ])
  }
}

/**
 * Assign an IP schema (POST /v1/network-profile/generate-ip-schema). `mutate({ idOrCode, storeCode, orgSlug })`.
 * Attaches the Bearer via the api-client (v1's worker-side no-auth variants are NOT copied).
 */
export function useGenerateIpSchema() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidateLocation()
  return useMutation({
    mutationFn: async (v: {
      idOrCode: string
      storeCode: string
      orgSlug: string
      /** Reissue over an existing schema; the API requires `edgeVendor` with it. */
      force?: boolean
      edgeVendor?: 'DELL' | 'CISCO' | 'MERAKI'
    }) => {
      if (preview) return { success: true }
      return api.request(
        '/v1/network-profile/generate-ip-schema',
        {
          method: 'POST',
          body: {
            storeCode: v.storeCode,
            orgSlug: v.orgSlug,
            ...(v.force ? { force: true } : {}),
            ...(v.edgeVendor ? { edgeVendor: v.edgeVendor } : {}),
          },
        },
        okEnvelopeSchema,
      )
    },
    onSuccess: (_r, v) => (preview ? undefined : invalidate(v.idOrCode)),
  })
}

/**
 * Register a device (POST /v1/device-onboarding). `mutate({ idOrCode, storeCode, orgSlug, input, confirmMove? })`.
 * The onboarding request schema has NO cross-store confirmation field (only the replace route does), so
 * `confirmMove` is accepted for call-site compatibility but is NOT sent (the API would strip it).
 */
export function useRegisterDevice() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidateLocation()
  return useMutation({
    mutationFn: async (v: {
      idOrCode: string
      storeCode: string
      orgSlug: string
      input: OnboardInput
      confirmMove?: boolean
    }) => {
      if (preview) return { success: true }
      const body = buildOnboardingBody(v.input, v.storeCode, v.orgSlug)
      return api.request('/v1/device-onboarding', { method: 'POST', body }, okEnvelopeSchema)
    },
    onSuccess: (_r, v) => (preview ? undefined : invalidate(v.idOrCode)),
  })
}

/** Remove a device (DELETE /v1/devices). `mutate({ idOrCode, locationId, deviceId, vendorSwap? })` (deviceId = node serial). */
export function useDeleteDevice() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidateLocation()
  return useMutation({
    mutationFn: async (v: { idOrCode: string; locationId: string; deviceId: string; vendorSwap?: boolean }) => {
      if (preview) return { success: true }
      return api.request(
        '/v1/devices',
        {
          method: 'DELETE',
          body: { locationId: v.locationId, deviceId: v.deviceId, vendorSwap: v.vendorSwap ?? false },
        },
        okEnvelopeSchema,
      )
    },
    onSuccess: (_r, v) => (preview ? undefined : invalidate(v.idOrCode, v.locationId)),
  })
}

const REPLACE_MANUFACTURERS = ['meraki', 'cloudflare', 'extreme', 'extreme networks', 'cradlepoint'] as const
type ReplaceManufacturer = (typeof REPLACE_MANUFACTURERS)[number]

/** Pure: DB node kind -> the replace API's `nodeKind` enum (ROUTER | SWITCH | AP | AUXILIARY). */
export function toReplaceNodeKind(kind: string): 'ROUTER' | 'SWITCH' | 'AP' | 'AUXILIARY' {
  const k = kind.toUpperCase()
  if (k === 'EDGE' || k.startsWith('ROUTER')) return 'ROUTER'
  if (k.startsWith('SWITCH')) return 'SWITCH'
  if (k === 'AP') return 'AP'
  return 'AUXILIARY'
}

/** Pure: free-text node manufacturer -> the replace API's `newManufacturer` enum, or undefined (like-for-like). */
export function toReplaceManufacturer(manufacturer?: string | null): ReplaceManufacturer | undefined {
  const m = manufacturer?.trim().toLowerCase()
  if (!m) return undefined
  if (m === 'dell') return 'cloudflare' // v1: a Dell edge is replaced by a Cloudflare connector
  return (REPLACE_MANUFACTURERS as readonly string[]).includes(m) ? (m as ReplaceManufacturer) : undefined
}

/** Pure: the `fromNetworkId` carried by a 409 CROSS_STORE_MIGRATION ApiError body, if any. */
export function crossStoreFromNetworkId(err: unknown): string | undefined {
  if (!isCrossStoreMigration(err)) return undefined
  const id = (err as { body?: { fromNetworkId?: unknown } }).body?.fromNetworkId
  return typeof id === 'string' ? id : undefined
}

/**
 * Pure: POST /v1/provision/:locationId/replace body. The API's confirmation fields are
 * `confirmCrossStoreMigration` + `confirmedFromNetworkId` (the network the 409 showed the operator; the API rejects
 * a confirmation without it). The designs' `extra.confirmMove` is translated here using `fromNetworkId`.
 */
export function buildReplaceBody(
  v: {
    oldNodeSerial: string
    newNodeSerial: string
    nodeKind: string
    newManufacturer?: string
    extra?: Record<string, unknown>
  },
  fromNetworkId?: string,
): Record<string, unknown> {
  const { confirmMove, ...extra } = v.extra ?? {}
  const body: Record<string, unknown> = {
    oldNodeSerial: v.oldNodeSerial,
    newNodeSerial: v.newNodeSerial,
    nodeKind: toReplaceNodeKind(v.nodeKind),
  }
  const manufacturer = toReplaceManufacturer(v.newManufacturer)
  if (manufacturer) body.newManufacturer = manufacturer
  if (confirmMove === true && fromNetworkId) {
    body.confirmCrossStoreMigration = true
    body.confirmedFromNetworkId = fromNetworkId
  }
  return { ...body, ...extra }
}

/**
 * Replace a device (POST /v1/provision/:locationId/replace). `nodeKind` / `newManufacturer` are normalised to the
 * API enums. A 409 `CROSS_STORE_MIGRATION` surfaces as ApiError (see `isCrossStoreMigration`); the hook remembers
 * its `fromNetworkId`, so re-submitting with `extra: { confirmMove: true }` sends `confirmCrossStoreMigration` +
 * `confirmedFromNetworkId` (explicit API fields in `extra` win).
 */
export function useReplaceDevice() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidateLocation()
  const fromNetworkId = useRef<string | undefined>(undefined)
  return useMutation({
    mutationFn: async (v: {
      idOrCode: string
      locationId: string
      oldNodeSerial: string
      newNodeSerial: string
      nodeKind: string
      newManufacturer?: string
      extra?: Record<string, unknown>
    }) => {
      if (preview) return { success: true }
      try {
        return await api.request(
          `/v1/provision/${encodeURIComponent(v.locationId)}/replace`,
          { method: 'POST', body: buildReplaceBody(v, fromNetworkId.current) },
          okEnvelopeSchema,
        )
      } catch (error) {
        const id = crossStoreFromNetworkId(error)
        if (id) fromNetworkId.current = id
        throw error
      }
    },
    onSuccess: (_r, v) => (preview ? undefined : invalidate(v.idOrCode, v.locationId)),
  })
}

/** Dispatch (POST /v1/provision/:locationId/dispatch `{override}`). A 4xx whose message mentions "override" needs `override: true`. */
export function useDispatchProvisioning() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidateLocation()
  return useMutation({
    mutationFn: async (v: { idOrCode: string; locationId: string; override?: boolean }) => {
      if (preview) return { success: true }
      return api.request(
        `/v1/provision/${encodeURIComponent(v.locationId)}/dispatch`,
        { method: 'POST', body: { override: v.override ?? false } },
        okEnvelopeSchema,
      )
    },
    onSuccess: (_r, v) => (preview ? undefined : invalidate(v.idOrCode, v.locationId)),
  })
}

/** Cancel dispatch (POST /v1/provision/:locationId/cancel). */
export function useCancelProvisioning() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidateLocation()
  return useMutation({
    mutationFn: async (v: { idOrCode: string; locationId: string }) => {
      if (preview) return { success: true }
      return api.request(
        `/v1/provision/${encodeURIComponent(v.locationId)}/cancel`,
        { method: 'POST' },
        okEnvelopeSchema,
      )
    },
    onSuccess: (_r, v) => (preview ? undefined : invalidate(v.idOrCode, v.locationId)),
  })
}

/** Pure: is this ApiError the 409 cross-store-migration prompt (so the design should show a confirm dialog)? */
export function isCrossStoreMigration(err: unknown): boolean {
  const e = err as { status?: number; body?: { code?: string } } | null
  return e?.status === 409 && e.body?.code === 'CROSS_STORE_MIGRATION'
}
